import { combineReducers } from "redux";
import { handleActions } from "redux-actions";
import * as actions from "./actions";

const setSession = (_, action) => action.payload.topikSession;

// Every mutation returns the full authoritative session; store it wholesale (§14 phase 3
// contract - no forced game/state re-pull, the 409-resync saga covers stale clients).
const session = handleActions(
  {
    [actions.session.get.success]: setSession,
    [actions.comparisons.generality.success]: setSession,
    [actions.comparisons.outcome.success]: setSession,
    [actions.analysis.observation.success]: setSession,
    [actions.analysis.seriousness.success]: setSession,
    [actions.analysis.issues.success]: setSession,
    [actions.analysis.risk.success]: setSession,
    [actions.analysis.solution.success]: setSession,
    [actions.analysis.continue.success]: setSession,
    [actions.analysis.riskMitigation.success]: setSession,
    [actions.analysis.solutionMaximizer.success]: setSession,
    [actions.analysis.goal.success]: setSession,
    [actions.analysis.back.success]: setSession,
  },
  null,
);

const initialStatus = {
  loading: false,
  submitting: false,
  error: null,
};

const loading = (state) => ({ ...state, loading: true, error: null });
const loaded = (state) => ({ ...state, loading: false });
const loadError = (state, action) => ({ ...state, loading: false, error: action.payload });

const submitting = (state) => ({ ...state, submitting: true, error: null });
const submitted = (state) => ({ ...state, submitting: false });
const submitError = (state, action) => ({ ...state, submitting: false, error: action.payload });

// Every mutating action shares the same submitting/submitted/error status transitions.
const MUTATIONS = [
  actions.comparisons.generality,
  actions.comparisons.outcome,
  actions.analysis.observation,
  actions.analysis.seriousness,
  actions.analysis.issues,
  actions.analysis.risk,
  actions.analysis.solution,
  actions.analysis.continue,
  actions.analysis.riskMitigation,
  actions.analysis.solutionMaximizer,
  actions.analysis.goal,
  actions.analysis.back,
];

const mutationHandlers = MUTATIONS.reduce((acc, triple) => ({
  ...acc,
  [triple.request]: submitting,
  [triple.success]: submitted,
  [triple.error]: submitError,
}), {});

const status = handleActions(
  {
    [actions.session.get.request]: loading,
    [actions.session.get.success]: loaded,
    [actions.session.get.error]: loadError,
    ...mutationHandlers,
  },
  initialStatus,
);

// Graduated AI step companion (§6.5). Separate from `session.aiAssist` (the availability +
// per-step used/cap payload): this slice tracks each step's in-flight request and latest
// parsed result, keyed by the client-side (camelCase) step id.
export const initialAssistStepState = {
  status: "idle",
  kind: null,
  text: null,
  suggestions: [],
  question: null,
  // Result-carried press count/cap: fresher than the session payload between mutations.
  used: null,
  cap: null,
  error: null,
};

const initialAiAssist = { byStep: {} };

const withStep = (state, step, stepState) => ({
  ...state,
  byStep: { ...state.byStep, [step]: stepState },
});

const applyAiAssistResult = (state, action) => {
  const { step } = action.payload;
  const result = action.payload.aiAssist || {};
  const current = state.byStep[step] || initialAssistStepState;
  return withStep(state, step, {
    ...current,
    status: result.status != null ? result.status : current.status,
    kind: result.kind != null ? result.kind : current.kind,
    text: result.text != null ? result.text : current.text,
    suggestions: result.suggestions != null ? result.suggestions : current.suggestions,
    question: result.question != null ? result.question : null,
    used: result.used != null ? result.used : current.used,
    cap: result.cap != null ? result.cap : current.cap,
    error: null,
  });
};

const applyAiAssistError = (state, action) => {
  const { step, error } = action.payload;
  const current = state.byStep[step] || initialAssistStepState;
  return withStep(state, step, { ...current, status: "error", error });
};

const aiAssist = handleActions(
  {
    // A fresh request clears the step's prior result so the panel does not show stale text.
    [actions.aiAssist.request.request]: (state, action) => (
      withStep(state, action.payload.step, { ...initialAssistStepState, status: "queued" })
    ),
    [actions.aiAssist.request.success]: applyAiAssistResult,
    [actions.aiAssist.request.error]: applyAiAssistError,
    [actions.aiAssist.poll.success]: applyAiAssistResult,
    [actions.aiAssist.poll.error]: applyAiAssistError,
  },
  initialAiAssist,
);

export const reducer = combineReducers({ session, status, aiAssist });
